import React, { useEffect, useRef, useState } from 'react';
import { OutlinedInput, type OutlinedInputProps } from '@mui/material';
import classNames from 'classnames';
import { Icons, type IconsType } from '../../../../assets/Icon';
import OpenAPI from '../../../../endpoint/Openapi';
import useFAQStore, { selectTopicName } from '../../../../stores/FAQStore';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';

const TopicIcons: Record<string, IconsType> = {
  '27682e84-0a44-479a-978f-c14a843891d8': 'briefcase',
  'd77c9194-331b-4224-aac7-16ce8113b17f': 'smartphone',
  '57665725-1e15-4e62-b6ac-b12a19417a9e': 'gift',
  'ad415a8d-e230-405c-9c25-126c575c0c8f': 'car',
  '2d8d41ea-75ec-499b-b808-def5b6fdcff5': 'clipboardCheck',
  '3a666504-02e9-4830-abf5-4c77f1b3da4e': 'userGroup',
  '58069a2d-be99-4548-b737-19f7015bf3a1': 'shieldCheck',
};

interface IFAQTopic
  extends Omit<OutlinedInputProps, 'value' | 'onChange' | 'name'> {
  id?: string;
  selected?: boolean;
  className?: string;
}

export default function FAQTopic({
  id: topicId,
  selected,
  ...props
}: IFAQTopic) {
  const tenantId = useTenantStore(selectTenantId);
  const topicName = useFAQStore((state) => selectTopicName(state, topicId));
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [value, setValue] = useState(topicName);
  const inputRef = useRef<HTMLInputElement>(null);
  const { updateTopic } = useFAQStore();

  useEffect(() => {
    setValue(topicName);
  }, [topicName]);

  function handleEdit() {
    setIsReadOnly(false);
    requestAnimationFrame(() => {
      inputRef.current?.select();
    });
  }

  async function handleUpdateTopic() {
    if (isReadOnly || !topicId) return;

    setIsReadOnly(true);

    const res = await OpenAPI.PUT('/api/v2/admin/faq/topic/{topicId}', {
      params: {
        path: {
          topicId,
        },
        query: {
          tenantId,
        },
      },
      body: {
        name: value,
        enabled: true,
      },
    });
    if (res.response.ok) {
      updateTopic({
        id: topicId,
        name: value,
      });
    }
  }

  function handleKeyDown(
    event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) {
    if (event.key === 'Enter') {
      // Handle the Enter key press
      void handleUpdateTopic();
    }
  }

  if (!topicId) return null;

  const TopicIcon = Icons[TopicIcons[topicId] ?? 'briefcase'];

  return (
    <OutlinedInput
      {...props}
      inputRef={inputRef}
      startAdornment={
        <TopicIcon
          className={`aspect-1 w-[3rem] mr-2 ${props.disabled ? 'text-gray' : 'text-primary'}`}
        />
      }
      endAdornment={
        isReadOnly ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit();
            }}
            disabled={props.disabled}
          >
            <Icons.edit className="aspect-1 w-[2rem] text-darkGray" />
          </button>
        ) : (
          <button onClick={handleUpdateTopic} disabled={props.disabled}>
            <Icons.check className="aspect-1 w-[2rem] text-success" />
          </button>
        )
      }
      readOnly={isReadOnly}
      className={classNames(
        'rounded-3xl bg-white h-14 max-w-96',
        props.className,
      )}
      style={{ borderRadius: '1.75rem', fontSize: '1em', ...props.style }}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      onClick={(e) => {
        e.stopPropagation();
        props.onClick?.(e);
      }}
      onKeyDown={handleKeyDown}
      onDoubleClick={handleEdit}
      onBlur={handleUpdateTopic}
      sx={{
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-gray)', // Default border color
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-stormGray)', // Border color on hover
          borderWidth: 2,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-stormGray)', // Border color on focus
          borderWidth: 2,
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-gray)', // Border color on focus
          borderWidth: 1,
        },
        ...(selected
          ? {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-stormGray)', // Default border color
                borderWidth: 2,
              },
            }
          : {}),
      }}
    />
  );
}
